import React, { useState, useEffect, useMemo, useRef } from "react";
import { CheckoutPage, CheckoutPageProps, SavedAddress } from "./CheckoutPage";
import { useCartStore } from "../cart/useCartStore";
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe as StripeJS } from "@stripe/stripe-js";
import { logStripeCheckoutEvent } from "./stripe-checkout-log";
import { savePendingStripeCheckout } from "./checkout-pending";
import { mapCartItemsToProductInfo } from "../cart/mapCartItems";
import type { Transaction } from "../client-ecommerce";

export interface CheckoutFlowProps {
    /** The authenticated user's ID. */
    userId?: string;
    /** The authenticated user's display name. */
    userDisplayName?: string;
    /** The authenticated user's email. */
    userEmail?: string;
    /** Async function to fetch saved addresses for the user. */
    onFetchAddresses: (userId: string) => Promise<SavedAddress[]>;
    /** Async function to save a new address (or update list). */
    onSaveAddress: (userId: string, addresses: SavedAddress[]) => Promise<void>;
    /** Async function to place the order (create transaction). */
    onPlaceOrder: (data: any) => Promise<Transaction>;
    /** Async function to finalize a Stripe order after payment (update shipping, clear cart). */
    onFinalizeStripeOrder: (
        transactionId: string,
        data: { shipping: Record<string, unknown>; paymentIntent?: { id: string; status: string } },
    ) => Promise<void>;
    /** Navigation callback to go back to cart. */
    onBackToCart: () => void;
    /** Callback after successful order placement — receives the new transaction id. */
    onOrderSuccess: (orderId: string) => void;
    /** Toast notification handler. */
    toast?: {
        success: (msg: string) => void;
        error: (msg: string) => void;
    };
    /**
     * Stripe publishable key fallback (pk_test_... or pk_live_...).
     * Checkout resolves the key from GET /api/stripe/config so it always
     * matches the server account that creates the PaymentIntent.
     */
    stripePublishableKey?: string;
    /**
     * Base URL for the API server (e.g. "http://localhost:3333").
     * Used to call `/api/stripe/create-payment-intent`.
     */
    apiBaseUrl?: string;
    /** Stripe return URL for redirect-based payment methods. */
    stripeReturnUrl?: string;
    /** Currency code for Stripe payments (default: "eur"). */
    currency?: string;
    /** Returns the current auth token for authenticated API calls. */
    getAuthToken?: () => Promise<string | null>;
    /** Locale for Stripe Elements (e.g. "en", "de", "fr"). */
    locale?: string;
    /** Translation function — English key in, localized string out. */
    t?: (key: string) => string;
}

export function CheckoutFlow({
    userId,
    userDisplayName,
    userEmail,
    onFetchAddresses,
    onSaveAddress,
    onPlaceOrder,
    onFinalizeStripeOrder,
    onBackToCart,
    onOrderSuccess,
    toast,
    stripePublishableKey,
    apiBaseUrl = "",
    stripeReturnUrl,
    currency = "eur",
    getAuthToken,
    locale,
    t: tProp,
}: CheckoutFlowProps) {
    const t = tProp ?? ((key: string) => key);
    const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
    const [stripePromise, setStripePromise] = useState<Promise<StripeJS | null> | null>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [pendingTransactionId, setPendingTransactionId] = useState<string | null>(null);
    const pendingTransactionIdRef = useRef<string | null>(null);
    const [resolvedPublishableKey, setResolvedPublishableKey] = useState<string | null>(
        stripePublishableKey ?? null,
    );
    const subtotal = useCartStore((s) => s.subtotal);
    const cartItems = useCartStore((s) => s.items);

    const stripeLogContext = useMemo(
        () => (apiBaseUrl && getAuthToken ? { apiBaseUrl, getAuthToken } : undefined),
        [apiBaseUrl, getAuthToken],
    );

    // 1. Fetch addresses on mount if user exists
    useEffect(() => {
        if (!userId) return;
        let mounted = true;
        onFetchAddresses(userId)
            .then((addrs) => {
                if (mounted) setSavedAddresses(addrs);
            })
            .catch((err) => console.error("Failed to fetch addresses:", err));
        return () => { mounted = false; };
    }, [userId, onFetchAddresses]);

    // 2. Use the publishable key from the API server (must match PaymentIntent account)
    useEffect(() => {
        if (!apiBaseUrl) {
            setResolvedPublishableKey(stripePublishableKey ?? null);
            return;
        }

        let mounted = true;
        fetch(`${apiBaseUrl}/api/stripe/config`)
            .then((r) => r.json())
            .then((data) => {
                if (!mounted) return;
                if (data.publishableKey) {
                    setResolvedPublishableKey(data.publishableKey);
                } else {
                    setResolvedPublishableKey(stripePublishableKey ?? null);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch Stripe config:", err);
                if (mounted) setResolvedPublishableKey(stripePublishableKey ?? null);
            });

        return () => { mounted = false; };
    }, [apiBaseUrl, stripePublishableKey]);

    // 3. Load Stripe.js once we have a publishable key
    useEffect(() => {
        if (!resolvedPublishableKey) return;
        setStripePromise(loadStripe(resolvedPublishableKey, { locale: (locale as any) || 'auto' }));
    }, [resolvedPublishableKey, locale]);

    // 4. Create a PaymentIntent when the flow mounts (so the form is ready)
    useEffect(() => {
        if (!resolvedPublishableKey || subtotal <= 0) return;
        let mounted = true;
        setClientSecret(null);

        // Stripe expects amount in smallest currency unit (cents for USD)
        const amountInCents = Math.round(subtotal * 100);

        // Get auth token for the API call (needed for Stripe Customer creation)
        const makeRequest = async () => {
            const headers: Record<string, string> = { "Content-Type": "application/json" };
            if (getAuthToken) {
                const token = await getAuthToken();
                if (token) headers["Authorization"] = `Bearer ${token}`;
            }
            const r = await fetch(`${apiBaseUrl}/api/stripe/create-payment-intent`, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    amount: amountInCents,
                    currency,
                    transaction_id: pendingTransactionIdRef.current ?? undefined,
                    product_info: mapCartItemsToProductInfo(cartItems),
                    total_amount: subtotal,
                }),
            });
            return r.json();
        };

        makeRequest()
            .then((data) => {
                if (!mounted) return;
                if (data.clientSecret) {
                    setClientSecret(data.clientSecret);
                    if (data.transactionId) {
                        pendingTransactionIdRef.current = data.transactionId;
                        setPendingTransactionId(data.transactionId);
                    }
                    if (stripeLogContext) {
                        void logStripeCheckoutEvent(stripeLogContext, 'payment_intent_client_secret_ready', {
                            amount: amountInCents,
                            currency,
                            transactionId: data.transactionId,
                        });
                    }
                } else if (data.error && stripeLogContext) {
                    void logStripeCheckoutEvent(stripeLogContext, 'payment_intent_create_failed', {
                        amount: amountInCents,
                        currency,
                        error: data.error,
                    }, 'error');
                }
            })
            .catch((err) => {
                console.error("Failed to create PaymentIntent:", err);
                if (stripeLogContext) {
                    void logStripeCheckoutEvent(stripeLogContext, 'payment_intent_create_failed', {
                        amount: amountInCents,
                        currency,
                        message: err instanceof Error ? err.message : String(err),
                    }, 'error');
                }
            });

        return () => { mounted = false; };
    }, [resolvedPublishableKey, apiBaseUrl, subtotal, currency, getAuthToken, stripeLogContext, cartItems]);

    // 5. Handle saving a new address
    const handleSaveAddress = async (address: any) => {
        if (!userId) return;
        try {
            // Re-fetch latest to be safe (or use local state if we trust it completely)
            const existing = await onFetchAddresses(userId);

            const newAddr: SavedAddress = {
                ...address,
                id: crypto.randomUUID(),
                label: address.address?.split(',')[0] || t('Checkout address'),
                phone: '',
                note: '',
                // If it's the first address, make it default
                isDefault: existing.length === 0,
            };

            const updated = [...existing, newAddr];
            await onSaveAddress(userId, updated);
            setSavedAddresses(updated);
            toast?.success(t("Address saved to your profile"));
        } catch (err) {
            console.error("Failed to save address:", err);
            toast?.error(t("Failed to save address"));
        }
    };

    // 6. Handle order placement (for non-Stripe methods)
    const handlePlaceOrder = async (data: any) => {
        try {
            const tx = await onPlaceOrder(data);
            toast?.success(t("Order placed successfully!"));
            onOrderSuccess(tx.id);
        } catch (err) {
            console.error("Failed to place order:", err);
            toast?.error(t("Failed to place order. Please try again."));
        }
    };

    const handleStripePaymentStart = (shipping: Record<string, unknown>) => {
        savePendingStripeCheckout({
            shipping: shipping as any,
            items: cartItems,
            subtotal,
            currency,
            transactionId: pendingTransactionId ?? undefined,
        });
    };

    // 7. Handle Stripe payment success (inline, no redirect)
    const handleStripePaymentSuccess = async (data: {
        shipping: any;
        paymentMethod: string;
        paymentIntent: { id: string; status: string;[key: string]: any };
    }) => {
        const orderId = pendingTransactionId;
        if (!orderId) {
            toast?.error(t("Payment succeeded but order could not be confirmed. Check My Purchases or contact support."));
            return;
        }

        try {
            await onFinalizeStripeOrder(orderId, {
                shipping: data.shipping,
                paymentIntent: data.paymentIntent,
            });
            if (stripeLogContext) {
                void logStripeCheckoutEvent(stripeLogContext, 'transaction_recorded', {
                    piId: data.paymentIntent.id,
                    status: data.paymentIntent.status,
                    transactionId: orderId,
                });
            }
            onOrderSuccess(orderId);
        } catch (err) {
            console.error("Failed to finalize Stripe transaction:", err);
            if (stripeLogContext) {
                void logStripeCheckoutEvent(stripeLogContext, 'transaction_record_failed', {
                    piId: data.paymentIntent.id,
                    message: err instanceof Error ? err.message : String(err),
                    transactionId: orderId,
                }, 'error');
            }
            toast?.error(t("Payment succeeded but failed to record transaction. Contact support."));
        }
    };

    const stripeSuccessReturnUrl = pendingTransactionId && stripeReturnUrl
        ? `${stripeReturnUrl}?order=${pendingTransactionId}`
        : stripeReturnUrl;

    return (
        <CheckoutPage
            onPlaceOrder={handlePlaceOrder}
            onSaveAddress={handleSaveAddress}
            onBackToCart={onBackToCart}
            savedAddresses={savedAddresses}
            initialShipping={{
                fullName: userDisplayName ?? "",
                email: userEmail ?? "",
            }}
            stripePromise={stripePromise}
            stripeClientSecret={clientSecret}
            onStripePaymentSuccess={handleStripePaymentSuccess}
            onStripePaymentStart={handleStripePaymentStart}
            stripeReturnUrl={stripeSuccessReturnUrl}
            stripeLogContext={stripeLogContext}
        />
    );
}
